/**
 * Cliente HTTP centralizado.
 *
 * - Adjunta el token JWT desde el store local.
 * - Propaga X-Request-Id si viene del backend (útil para soporte).
 * - Timeout 15s por defecto.
 * - Retry con backoff para 5xx/errores de red en métodos idempotentes.
 * - Exposición de `AbortController` via helper `newAbort()` para cancelación.
 *
 * Los módulos deben usar `httpClient` (esta exportación) o seguir usando
 * el reexport compatible en `src/shared/services/api.ts` durante la migración.
 */
import axios, { AxiosError, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';

import { registerInterceptors } from './interceptors';

const API_URL = (import.meta.env.VITE_API_URL as string | undefined) || 'http://localhost:8000';

const DEFAULT_TIMEOUT_MS = 15_000;
const MAX_RETRIES = 2;
const BASE_BACKOFF_MS = 400;

export const httpClient = axios.create({
  baseURL: API_URL,
  timeout: DEFAULT_TIMEOUT_MS,
  headers: {
    'Content-Type': 'application/json',
  },
});

type RetryableConfig = InternalAxiosRequestConfig & { _retryCount?: number };

function shouldRetry(error: AxiosError): boolean {
  const config = error.config as RetryableConfig | undefined;
  if (!config) return false;
  const method = (config.method || 'get').toLowerCase();
  if (!['get', 'head', 'options'].includes(method)) return false;

  const status = error.response?.status;
  // Sin respuesta = error de red / timeout.
  if (status === undefined) return error.code !== 'ERR_CANCELED';
  return status >= 500 && status < 600;
}

httpClient.interceptors.response.use(
  (res) => res,
  async (error: AxiosError) => {
    const config = error.config as RetryableConfig | undefined;
    if (!config || !shouldRetry(error)) {
      return Promise.reject(error);
    }
    const current = config._retryCount ?? 0;
    if (current >= MAX_RETRIES) {
      return Promise.reject(error);
    }
    config._retryCount = current + 1;
    const delay = BASE_BACKOFF_MS * 2 ** current;
    await new Promise((r) => setTimeout(r, delay));
    return httpClient(config);
  }
);

registerInterceptors(httpClient);

/**
 * Crea un `AbortController` y devuelve un `config` de axios listo para pasar
 * a una request. Útil para cancelar al desmontar componentes.
 *
 * Ejemplo:
 *   const { signal, abort } = newAbort();
 *   api.get('/leads', { signal });
 *   return () => abort();
 */
export function newAbort(timeoutMs?: number) {
  const ctrl = new AbortController();
  const timer = timeoutMs ? setTimeout(() => ctrl.abort(), timeoutMs) : null;
  return {
    signal: ctrl.signal,
    abort: () => {
      if (timer) clearTimeout(timer);
      ctrl.abort();
    },
  };
}

export type { AxiosRequestConfig };
export default httpClient;
